import { useState, useEffect } from "react";
import { Button, Group, Text, Alert } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons";
import { useServerApi } from "../hooks/userServerApi";
import _ from "lodash";
import { useFormatter } from "../hooks/useFomatter";
import CommissionSettingOverridingCard from "./commissionSettingOverridingCard";
import CommissionSettingFixAmountSchedule from "./commissionSettingFixAmountSchedule";
import { showNotification } from "@mantine/notifications";
import { useAuthUser } from "react-auth-kit";

const resolveRefId = (ref) => (_.isObject(ref) ? ref._id : ref) || null;

const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return "N/A";
  return `HKD ${Number(amount).toLocaleString("en-HK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getSplitModeLabel = (tree) => {
  if (tree?.splitByCommissionRate) return "By Comm. Rate %";
  if (tree?.splitByAmount) return "By Amount";
  return "By Level %";
};

const getRowLabel = (row) => {
  if (row.user?.name) return `${row.user.name} [${row.user.staffId ?? ""}]`;
  if (row.userGroup?.name) return row.userGroup.name;
  return row.levelType ?? `Level ${row.level ?? row.index}`;
};

const getRowKey = (row) => {
  if (row?.user?._id) return `user:${row.user._id}`;
  if (row?.userGroup?._id) return `group:${row.userGroup._id}`;
  return `idx:${row?.index ?? "?"}`;
};

const buildTableRowMap = (table) => {
  const map = new Map();
  for (const row of table) {
    map.set(getRowKey(row), row);
  }
  return map;
};

const buildCommissionOverrideChanges = (oldSetting, newSetting) => {
  const changes = [];

  if (
    (oldSetting?.isCustomizedOverriding ?? false) !==
    (newSetting?.isCustomizedOverriding ?? false)
  ) {
    changes.push({
      fieldLabel: "Customized Override",
      oldValue: oldSetting?.isCustomizedOverriding ? "Yes" : "No",
      newValue: newSetting?.isCustomizedOverriding ? "Yes" : "No",
    });
  }

  const oldYears = oldSetting?.salesOverriding ?? [];
  const newYears = newSetting?.salesOverriding ?? [];
  const maxYears = Math.max(oldYears.length, newYears.length);

  for (let y = 0; y < maxYears; y++) {
    const oldYear = oldYears[y];
    const newYear = newYears[y];
    const yearLabel = (oldYear?.year ?? newYear?.year ?? `yr${y + 1}`).toUpperCase();

    if ((oldYear?.yearCommission ?? 0) !== (newYear?.yearCommission ?? 0)) {
      changes.push({
        fieldLabel: `${yearLabel} - Year Commission`,
        oldValue: formatCurrency(oldYear?.yearCommission),
        newValue: formatCurrency(newYear?.yearCommission),
      });
    }

    if ((oldYear?.rate ?? 0) !== (newYear?.rate ?? 0)) {
      changes.push({
        fieldLabel: `${yearLabel} - Gross Rate`,
        oldValue: `${((oldYear?.rate ?? 0) * 100).toFixed(2)}%`,
        newValue: `${((newYear?.rate ?? 0) * 100).toFixed(2)}%`,
      });
    }

    const oldTrees = oldYear?.trees ?? [];
    const newTrees = newYear?.trees ?? [];
    const maxTrees = Math.max(oldTrees.length, newTrees.length);

    for (let t = 0; t < maxTrees; t++) {
      const oldTree = oldTrees[t];
      const newTree = newTrees[t];
      const consultantName =
        oldTree?.consultant?.name ?? newTree?.consultant?.name ?? `Tree ${t + 1}`;
      const prefix = `${yearLabel} ${consultantName}`;

      if ((oldTree?.treeSplitPercent ?? 0) !== (newTree?.treeSplitPercent ?? 0)) {
        changes.push({
          fieldLabel: `${prefix} - Split`,
          oldValue: `${oldTree?.treeSplitPercent ?? 0}%`,
          newValue: `${newTree?.treeSplitPercent ?? 0}%`,
        });
      }

      const oldPlanName = oldTree?.plan?.name ?? "";
      const newPlanName = newTree?.plan?.name ?? "";
      if (oldPlanName !== newPlanName) {
        changes.push({
          fieldLabel: `${prefix} - Plan`,
          oldValue: oldPlanName || "N/A",
          newValue: newPlanName || "N/A",
        });
      }

      const oldSplitMode = getSplitModeLabel(oldTree);
      const newSplitMode = getSplitModeLabel(newTree);
      if (oldSplitMode !== newSplitMode) {
        changes.push({
          fieldLabel: `${prefix} - Split Mode`,
          oldValue: oldSplitMode,
          newValue: newSplitMode,
        });
      }

      if ((oldTree?.commission ?? 0) !== (newTree?.commission ?? 0)) {
        changes.push({
          fieldLabel: `${prefix} - Commission`,
          oldValue: formatCurrency(oldTree?.commission),
          newValue: formatCurrency(newTree?.commission),
        });
      }

      if ((oldTree?.premium ?? 0) !== (newTree?.premium ?? 0)) {
        changes.push({
          fieldLabel: `${prefix} - Premium`,
          oldValue: formatCurrency(oldTree?.premium),
          newValue: formatCurrency(newTree?.premium),
        });
      }

      const oldTable = oldTree?.table ?? [];
      const newTable = newTree?.table ?? [];
      const oldMap = buildTableRowMap(oldTable);
      const newMap = buildTableRowMap(newTable);
      const allKeys = new Set([...oldMap.keys(), ...newMap.keys()]);

      for (const key of allKeys) {
        const oldRow = oldMap.get(key);
        const newRow = newMap.get(key);
        const rowLabel = getRowLabel(oldRow ?? newRow ?? {});

        if (!oldRow) {
          changes.push({
            fieldLabel: `${prefix} - ${rowLabel}`,
            oldValue: "",
            newValue: `Added (${(newRow.percentage ?? 0).toFixed(2)}% | ${formatCurrency(newRow.amount)})`,
          });
          continue;
        }
        if (!newRow) {
          changes.push({
            fieldLabel: `${prefix} - ${rowLabel}`,
            oldValue: `Removed (${(oldRow.percentage ?? 0).toFixed(2)}% | ${formatCurrency(oldRow.amount)})`,
            newValue: "",
          });
          continue;
        }

        if ((oldRow.percentage ?? 0) !== (newRow.percentage ?? 0)) {
          changes.push({
            fieldLabel: `${prefix} - ${rowLabel} Level %`,
            oldValue: `${(oldRow.percentage ?? 0).toFixed(2)}%`,
            newValue: `${(newRow.percentage ?? 0).toFixed(2)}%`,
          });
        }

        if ((oldRow.commissionRate ?? 0) !== (newRow.commissionRate ?? 0)) {
          changes.push({
            fieldLabel: `${prefix} - ${rowLabel} Comm Rate`,
            oldValue: `${(oldRow.commissionRate ?? 0).toFixed(2)}%`,
            newValue: `${(newRow.commissionRate ?? 0).toFixed(2)}%`,
          });
        }

        if ((oldRow.amount ?? 0) !== (newRow.amount ?? 0)) {
          changes.push({
            fieldLabel: `${prefix} - ${rowLabel} Amt`,
            oldValue: formatCurrency(oldRow.amount),
            newValue: formatCurrency(newRow.amount),
          });
        }
      }
    }
  }

  return changes;
};

const DebitNoteCommissionSetting = ({ form, readonly = false }) => {
  const debitNoteId = form.values._id;
  const orderId = resolveRefId(form.values.order);
  const reloadKey = form._reloadKey ?? 0;

  const [api] = useServerApi();
  const auth = useAuthUser();
  const [commissionSetting, setCommissionSetting] = useState(null);
  const [overrideMode, setOverrideMode] = useState("view");
  const [formatter] = useFormatter();
  const [disableUpdate, setDisableUpdate] = useState(false);
  const [sharedOrderSiblings, setSharedOrderSiblings] = useState([]);

  const checkDisableUpdate = async () => {
    if (!orderId) return;
    const isReleasing = await api.CommissionServer.checkReleasingBids(orderId);
    setDisableUpdate(isReleasing);
  };

  const loadSharedOrderSiblings = async (resolvedOrderId) => {
    if (!resolvedOrderId || !debitNoteId) {
      setSharedOrderSiblings([]);
      return;
    }

    try {
      const result = await api.search({
        apiEntity: "debitNote",
        pageSize: 20,
        searchQuery: { order: resolvedOrderId },
      });
      const siblings = (result?.docs ?? []).filter(
        (dn) => String(dn._id) !== String(debitNoteId),
      );
      setSharedOrderSiblings(siblings);
    } catch (error) {
      console.error("Error loading shared-order debit notes:", error);
      setSharedOrderSiblings([]);
    }
  };

  useEffect(() => {
    checkDisableUpdate();
    loadSharedOrderSiblings(orderId);
  }, [orderId, debitNoteId, reloadKey]);

  const fetchCommissionSetting = async () => {
    try {
      // console.log("fetchCommissionSetting > orderId:", orderId, debitNoteId);
      if (!orderId && !debitNoteId) return;

      let order = null;
      if (!orderId) {
        let result = await api.search({
          apiEntity: "order",
          pageSize: 1,
          searchQuery: {
            debitNote: debitNoteId,
          },
        });
        const orders = result.docs;
        if (orders.length > 0) {
          order = orders[0];
        }
      } else {
        order = await api.getById({
          apiEntity: "order",
          id: orderId,
        });
      }

      if (!order) return;

      // console.log("fetchCommissionSetting > order:", order.commissionSetting);
      const result = await api.getById({
        apiEntity: "commissionSetting",
        id: order.commissionSetting,
      });
      // console.log("fetchCommissionSetting > data:", orderId, result);
      setCommissionSetting(result);
      return result;
    } catch (error) {
      console.error("Error fetching commission setting:", error);
      return null;
    }
  };

  useEffect(() => {
    fetchCommissionSetting();
  }, [orderId, debitNoteId, reloadKey]);

  const requireOrderId = () => {
    if (orderId) return orderId;
    showNotification({
      title: "Error",
      message: "Order is not linked to this debit note yet.",
      color: "red",
    });
    return null;
  };

  //Get the sales overriding for the order
  const resetCommissionSettingOverride = async () => {
    try {
      const resolvedOrderId = requireOrderId();
      if (!resolvedOrderId) return;
      const oldSetting = _.cloneDeep(commissionSetting);

      await api.CommissionServer.resetCommissionSettingOverride(resolvedOrderId);
      const newSetting = await fetchCommissionSetting();

      if (debitNoteId && oldSetting && newSetting) {
        const changes = buildCommissionOverrideChanges(oldSetting, newSetting);
        await api.addActivityLog({
          entityId: debitNoteId,
          title: "Commission Override Reset",
          by: auth()?.id,
          description: changes.length > 0
            ? `${changes.length} field(s) changed`
            : "No changes detected",
          changes,
        });
      }
    } catch (error) {
      console.error("Error resetting commission setting override:", error);
      showNotification({
        title: "Error",
        message: error.message,
        color: "red",
      });
    }
  };

  const onCustomizeOverriding = async () => {
    await api.updateById({
      apiEntity: "commissionSetting",
      id: commissionSetting._id,
      values: { isCustomizedOverriding: true },
    });

    if (debitNoteId) {
      await api.addActivityLog({
        entityId: debitNoteId,
        title: "Commission Override Customized",
        by: auth()?.id,
        description: "Commission override entered customize mode",
        changes: [
          {
            fieldLabel: "Customized Override",
            oldValue: "No",
            newValue: "Yes",
          },
        ],
      });
    }

    fetchCommissionSetting();
    setOverrideMode("edit");
  };

  const onUpdateCommissionSettingOverride = async () => {
    try {
      const resolvedOrderId = requireOrderId();
      if (!resolvedOrderId) return;
      const oldSetting = _.cloneDeep(commissionSetting);

      commissionSetting.isCustomizedOverriding = true;
      await api.updateById({
        apiEntity: "commissionSetting",
        id: commissionSetting._id,
        values: commissionSetting,
      });

      await api.CommissionServer.updateCommissionSettingOverride(resolvedOrderId);
      const newSetting = await fetchCommissionSetting();
      setOverrideMode("view");

      if (debitNoteId && oldSetting && newSetting) {
        const changes = buildCommissionOverrideChanges(oldSetting, newSetting);
        await api.addActivityLog({
          entityId: debitNoteId,
          title: "Commission Override Updated",
          by: auth()?.id,
          description: changes.length > 0
            ? `${changes.length} field(s) changed`
            : "No changes detected",
          changes,
        });
      }
    } catch (error) {
      console.error("Error recalculating sales overriding:", error);
    }
  };

  return (
    <>
      {sharedOrderSiblings.length > 0 && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title="Shared order"
          color="yellow"
          mb="md"
        >
          This debit note shares order{" "}
          {(_.isObject(form.values.order) ? form.values.order.code : null) ??
            orderId}{" "}
          with:{" "}
          {sharedOrderSiblings.map((dn) => dn.code).join(", ")}.{" "}
          {form.values.type === "CREDIT"
            ? "Commission is shared until this credit note is moved to its own order."
            : "Commission settings apply to all debit notes on the same order."}
        </Alert>
      )}

      {/* Fix commission amount */}
      <CommissionSettingFixAmountSchedule
        commissionSetting={commissionSetting}
        onRefresh={fetchCommissionSetting}
        disableUpdate={disableUpdate}
      />

      <CommissionSettingOverridingCard
        commissionSetting={commissionSetting}
        onResetOverride={resetCommissionSettingOverride}
        onCustomize={onCustomizeOverriding}
        onUpdateOverride={onUpdateCommissionSettingOverride}
        isCustomized={commissionSetting?.isCustomizedOverriding}
        mode={overrideMode}
        disableUpdate={disableUpdate}
        readonly={readonly}
      />
    </>
  );
};

export default DebitNoteCommissionSetting;
